import { Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoService } from '@jsverse/transloco';
import { EnumsService } from '../../../services/enums.service';
import { Liability } from '../../../models/mortgage.model';

export interface LienOwnerOption {
  id: number;
  name: string;
}

/**
 * LLS-569 item 3: "Add Existing Lien" modal. Edits the lien it is given in place (the opener
 * passes a fresh lien or a clone of an existing one) and emits it on Save; the opener decides
 * where it is stored. Holder name is required so reo-liability-sync never mistakes a modal lien
 * for its own auto-created placeholder.
 */
@Component({
    selector: 'lien-dialog',
    templateUrl: 'lien-dialog.component.html',
    styleUrls: ['./lien-dialog.component.scss'],
    standalone: false
})
export class LienDialogComponent {

  private readonly _transloco = inject(TranslocoService);

  @ViewChild('lienForm') lienForm: NgForm | undefined;

  @Input()
  lien!: Liability;

  @Input()
  inEditMode: boolean = false;

  /** In the co-apply flow the opener sets the owner to the current borrower and the picker is hidden. */
  @Input()
  inCoApplyFlow: boolean = false;

  @Input()
  possibleOwners: LienOwnerOption[] = [];

  @Output()
  saveClicked = new EventEmitter<Liability>();

  /** Set once Save is attempted so the owner picker can show its own required message. */
  ownersTouched: boolean = false;

  // The lien dialog stacks on top of the REO dialog, whose owner picker uses borrower names as
  // element ids, so every id here carries a per-instance suffix.
  uniqueId: number = Math.abs(Date.now() & 0xffffffff);

  constructor(public activeModal: NgbActiveModal,
    private readonly _enumsService: EnumsService) {
  }

  get lienPositionTypes(): { name: string; value: string }[] {
    return this._enumsService.lienPositionTypes;
  }

  get header(): string {
    return this._transloco.translate(this.inEditMode ? 'wizard.reo.editLien' : 'wizard.reo.addExistingLien');
  }

  get hasOwner(): boolean {
    return (this.lien?.owningBorrowerIds ?? []).length > 0;
  }

  isOwner = (borrowerId: number): boolean => {
    return (this.lien?.owningBorrowerIds ?? []).includes(borrowerId);
  }

  onOwnerToggled = (borrowerId: number, checked: boolean): void => {
    const owners = this.lien.owningBorrowerIds ?? [];
    if (checked) {
      if (!owners.includes(borrowerId)) {
        owners.push(borrowerId);
      }
    } else {
      const index = owners.indexOf(borrowerId);
      if (index >= 0) {
        owners.splice(index, 1);
      }
    }
    this.lien.owningBorrowerIds = owners;
  }

  onSaveClicked = (): void => {
    this.lienForm?.form.markAllAsTouched();
    this.ownersTouched = true;
    const ownersValid = this.inCoApplyFlow || this.hasOwner;
    if (!this.lienForm?.form.valid || !ownersValid) {
      return;
    }
    this.activeModal.close();
    this.saveClicked.emit(this.lien);
  }
}
